// Settings and configuration management
import AuthManager from './auth.js';

class SettingsManager {
    constructor() {
        this.settings = {};
        this.personas = {};
        this.documentTypes = {};
        this.isDirty = false;
    }

    // Load all configuration
    async loadConfiguration() {
        try {
            const [personas, documentTypes] = await Promise.all([
                AuthManager.get('/api/config/personas').then(r => r.json()),
                AuthManager.get('/api/document-types').then(r => r.json())
            ]);

            this.personas = personas;
            this.documentTypes = documentTypes;
            
            return { personas, documentTypes };
        } catch (error) {
            console.error('Failed to load configuration:', error);
            throw error;
        }
    }

    // Show settings modal
    async showSettingsModal() {
        await this.loadConfiguration();
        
        const modal = document.createElement('div');
        modal.className = 'modal settings-modal';
        modal.style.display = 'flex';
        
        modal.innerHTML = `
            <div class="modal-content large">
                <div class="modal-header">
                    <h3>Document Types</h3>
                    <button class="close-btn">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="settings-content">
                        <div id="document-types-tab" class="tab-content active">
                            ${this.renderDocumentTypesSettings()}
                        </div>
                    </div>
                </div>
            </div>
        `;

        document.body.appendChild(modal);
        document.body.style.overflow = 'hidden';
        this.setupSettingsModalEvents(modal);
    }



    renderDocumentTypesSettings() {
        const builtInTypes = this.documentTypes.filter(dt => dt.isBuiltIn);
        const customTypes = this.documentTypes.filter(dt => dt.isCustom);
        
        return `
            <div class="settings-section">
                <div class="section-header">
                    <h4>Document Types</h4>
                    <button class="btn btn-primary btn-sm" id="add-document-type-btn">+ Create Custom Type</button>
                </div>
                
                <div class="document-types-section">
                    <h5 style="margin-top: 1.5rem; margin-bottom: 0.75rem; color: #6c757d; font-size: 0.9rem;">Built-in Types</h5>
                    <div class="document-types-list">
                        ${builtInTypes.map(docType => `
                            <div class="document-type-item" data-id="${docType.id}">
                                <div class="document-type-header">
                                    <h5>${docType.name}</h5>
                                    <div class="document-type-actions">
                                        <button class="btn btn-sm clone-document-type-btn" data-id="${docType.id}">Clone</button>
                                    </div>
                                </div>
                                <p class="document-type-description">${docType.description}</p>
                            </div>
                        `).join('')}
                    </div>
                </div>
                
                ${customTypes.length > 0 ? `
                    <div class="document-types-section">
                        <h5 style="margin-top: 1.5rem; margin-bottom: 0.75rem; color: #6c757d; font-size: 0.9rem;">My Custom Types</h5>
                        <div class="document-types-list">
                            ${customTypes.map(docType => `
                                <div class="document-type-item custom" data-id="${docType.id}">
                                    <div class="document-type-header">
                                        <h5>⭐ ${docType.name}</h5>
                                        <div class="document-type-actions">
                                            <button class="btn btn-sm edit-document-type-btn" data-id="${docType.id}">Edit</button>
                                            <button class="btn btn-sm btn-danger delete-document-type-btn" data-id="${docType.id}">Delete</button>
                                        </div>
                                    </div>
                                    <p class="document-type-description">${docType.description}</p>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                ` : ''}
            </div>
        `;
    }

    setupSettingsModalEvents(modal) {
        // Close modal
        modal.querySelector('.close-btn').addEventListener('click', () => {
            document.body.style.overflow = '';
            document.body.removeChild(modal);
        });

        // Document type management
        modal.querySelector('#add-document-type-btn')?.addEventListener('click', () => {
            this.showDocumentTypeEditor();
        });

        this.setupDocumentTypeEvents(modal);
    }

    showPersonaEditor(personaKey = null) {
        const isEdit = personaKey !== null;
        const persona = isEdit ? this.personas[personaKey] : {
            name: '',
            description: '',
            planPrompt: '',
            actPrompt: ''
        };

        const modal = document.createElement('div');
        modal.className = 'modal persona-editor-modal';
        modal.style.display = 'flex';
        
        modal.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <h3>${isEdit ? 'Edit' : 'Add'} Persona</h3>
                    <button class="close-btn">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="form-group">
                        <label for="persona-name">Name *</label>
                        <input type="text" id="persona-name" value="${persona.name}" required>
                    </div>
                    
                    <div class="form-group">
                        <label for="persona-description">Description</label>
                        <textarea id="persona-description" rows="3">${persona.description}</textarea>
                    </div>
                    
                    <div class="form-group">
                        <label for="persona-plan-prompt">Plan Mode Prompt</label>
                        <textarea id="persona-plan-prompt" rows="5">${persona.planPrompt}</textarea>
                    </div>
                    
                    <div class="form-group">
                        <label for="persona-act-prompt">Act Mode Prompt</label>
                        <textarea id="persona-act-prompt" rows="5">${persona.actPrompt}</textarea>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary cancel-btn">Cancel</button>
                    <button type="button" class="btn btn-primary save-btn">${isEdit ? 'Update' : 'Create'}</button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Event handlers
        modal.querySelector('.close-btn').addEventListener('click', () => {
            document.body.removeChild(modal);
        });

        modal.querySelector('.cancel-btn').addEventListener('click', () => {
            document.body.removeChild(modal);
        });

        modal.querySelector('.save-btn').addEventListener('click', async () => {
            await this.savePersona(modal, personaKey);
        });
    }

    async savePersona(modal, personaKey) {
        const personaData = {
            name: modal.querySelector('#persona-name').value,
            description: modal.querySelector('#persona-description').value,
            planPrompt: modal.querySelector('#persona-plan-prompt').value,
            actPrompt: modal.querySelector('#persona-act-prompt').value
        };

        if (!personaData.name) {
            alert('Persona name is required');
            return;
        }

        try {
            const isEdit = personaKey !== null;
            const key = personaKey || personaData.name.toLowerCase().replace(/\s+/g, '-');
            
            const url = isEdit ? `/api/config/personas/${key}` : '/api/config/personas';
            const method = isEdit ? 'PUT' : 'POST';
            const body = isEdit ? personaData : { key, ...personaData };

            const response = isEdit 
                ? await AuthManager.put(url, body)
                : await AuthManager.post(url, body);

            if (response.ok) {
                this.personas[key] = personaData;
                document.body.removeChild(modal);
                this.showSuccessMessage(`Persona ${isEdit ? 'updated' : 'created'} successfully!`);
                
                // Refresh settings modal if open
                const settingsModal = document.querySelector('.settings-modal');
                if (settingsModal) {
                    settingsModal.querySelector('#personas-tab').innerHTML = this.renderPersonasSettings();
                }
            } else {
                throw new Error(`Failed to ${isEdit ? 'update' : 'create'} persona`);
            }

        } catch (error) {
            console.error('Error saving persona:', error);
            alert(`Failed to ${personaKey ? 'update' : 'create'} persona. Please try again.`);
        }
    }

    showSuccessMessage(message) {
        const notification = document.createElement('div');
        notification.className = 'file-operation-feedback';
        notification.innerHTML = `<strong>Success!</strong><p>${message}</p>`;

        document.body.appendChild(notification);

        setTimeout(() => {
            if (notification.parentElement) {
                document.body.removeChild(notification);
            }
        }, 3000);
    }

    refreshHomePageDocumentTypes() {
        // Check if we're on the home page with document type cards
        const docTypeCards = document.getElementById('document-type-cards');
        if (docTypeCards && window.app) {
            // Update app's config with new document types
            window.app.config.documentTypes = this.documentTypes;
            // Get currently selected persona
            const selectedPersonaCard = document.querySelector('#persona-cards .selection-card.selected');
            const selectedPersona = selectedPersonaCard ? selectedPersonaCard.dataset.value : null;
            // Re-render document type cards
            window.app.updateDocumentTypeCards(selectedPersona);
        }
    }

    // Document Type Management
    async showDocumentTypeEditor(typeId = null, cloneData = null, preSelectedPersona = null) {
        const isEdit = !!typeId;
        const isClone = !!cloneData;
        let docType = cloneData;

        if (isEdit) {
            // Fetch full document type details
            const response = await AuthManager.get(`/api/document-types/${typeId}`);
            if (!response.ok) {
                alert('Failed to load document type');
                return;
            }
            docType = await response.json();
        }

        // Determine which persona should be selected
        const selectedPersona = docType?.persona || preSelectedPersona || '';

        const modal = document.createElement('div');
        modal.className = 'modal document-type-editor-modal';
        modal.style.display = 'flex';

        modal.innerHTML = `
            <div class="modal-content fullscreen">
                <div class="modal-header">
                    <h3>${isEdit ? 'Edit' : isClone ? 'Clone' : 'Create'} Document Type</h3>
                    <button class="close-btn">&times;</button>
                </div>
                <div class="modal-body document-type-editor-3col">
                    <div class="editor-col-1">
                        <div class="form-group">
                            <label for="doctype-name">Name *</label>
                            <input type="text" id="doctype-name" value="${isClone ? (docType?.name + ' (Copy)') : (docType?.name || '')}" required>
                        </div>

                        <div class="form-group">
                            <label for="doctype-description">Description *</label>
                            <textarea id="doctype-description" rows="10" required>${docType?.description || ''}</textarea>
                            <button type="button" class="btn btn-secondary ai-generate-btn" id="ai-generate-prompts-btn" style="margin-top: 0.5rem;">
                                <span class="btn-icon">✨</span> Generate from Description
                            </button>
                            <small style="color: #666; display: block; margin-top: 0.5rem;">Generate plan and act prompts using AI</small>
                        </div>

                        <div class="form-group">
                            <label for="doctype-persona">Persona *</label>
                            <select id="doctype-persona" required>
                                <option value="">Select persona...</option>
                                ${Object.entries(this.personas).map(([key, persona]) => 
                                    `<option value="${key}" ${key === selectedPersona ? 'selected' : ''}>${persona.name}</option>`
                                ).join('')}
                            </select>
                        </div>

                        <div class="form-actions">
                            <div class="form-actions-left">
                                ${isEdit ? '<button class="btn btn-danger delete-doctype-btn">Delete</button>' : ''}
                            </div>
                            <div class="form-actions-right">
                                <button class="btn cancel-btn">Cancel</button>
                                <button class="btn btn-primary save-doctype-btn">${isEdit ? 'Update' : 'Create'}</button>
                            </div>
                        </div>
                    </div>

                    <div class="editor-col-2">
                        <div class="form-group full-height">
                            <label for="doctype-plan-prompt">Plan Mode Prompt * <small style="color: #666; font-weight: normal;">AI asks questions to gather information</small></label>
                            <textarea id="doctype-plan-prompt" required placeholder="Instructions for gathering information in plan mode...">${docType?.planPrompt || ''}</textarea>
                        </div>
                    </div>

                    <div class="editor-col-3">
                        <div class="form-group full-height">
                            <label for="doctype-act-prompt">Act Mode Prompt * <small style="color: #666; font-weight: normal;">AI creates the document from gathered info</small></label>
                            <textarea id="doctype-act-prompt" required placeholder="Instructions for creating the document in act mode...">${docType?.actPrompt || ''}</textarea>
                        </div>
                    </div>
                </div>
            </div>
        `;

        document.body.appendChild(modal);
        document.body.style.overflow = 'hidden';

        // Event handlers
        modal.querySelector('.close-btn').addEventListener('click', () => {
            document.body.style.overflow = '';
            document.body.removeChild(modal);
        });
        modal.querySelector('.cancel-btn').addEventListener('click', () => {
            document.body.style.overflow = '';
            document.body.removeChild(modal);
        });

        // Delete (only for edit mode)
        if (isEdit) {
            modal.querySelector('.delete-doctype-btn')?.addEventListener('click', async () => {
                await this.deleteDocumentType(typeId);
                document.body.style.overflow = '';
                document.body.removeChild(modal);
            });
        }

        // AI Generate prompts
        modal.querySelector('#ai-generate-prompts-btn').addEventListener('click', async () => {
            await this.generatePromptsWithAI(modal);
        });

        // Save
        modal.querySelector('.save-doctype-btn').addEventListener('click', async () => {
            await this.saveDocumentType(modal, typeId);
            document.body.style.overflow = '';
        });
    }

    async generatePromptsWithAI(modal) {
        const name = modal.querySelector('#doctype-name').value.trim();
        const description = modal.querySelector('#doctype-description').value.trim();
        const persona = modal.querySelector('#doctype-persona').value;

        if (!name || !description) {
            alert('Please provide both a name and description before generating prompts');
            return;
        }

        if (!persona) {
            alert('Please select a persona before generating prompts');
            return;
        }

        const generateBtn = modal.querySelector('#ai-generate-prompts-btn');
        const originalText = generateBtn.innerHTML;
        
        try {
            // Show loading state
            generateBtn.disabled = true;
            generateBtn.innerHTML = '<span class="btn-icon">⏳</span> Generating...';

            // Call backend to generate prompts
            const response = await AuthManager.post('/api/document-types/generate-prompts', {
                name,
                description,
                persona
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to generate prompts');
            }

            const result = await response.json();

            // Update the textareas with generated prompts
            modal.querySelector('#doctype-plan-prompt').value = result.planPrompt;
            modal.querySelector('#doctype-act-prompt').value = result.actPrompt;

            this.showSuccessMessage('Prompts generated successfully!');

        } catch (error) {
            console.error('Error generating prompts:', error);
            alert(`Failed to generate prompts: ${error.message}`);
        } finally {
            // Reset button state
            generateBtn.disabled = false;
            generateBtn.innerHTML = originalText;
        }
    }

    async saveDocumentType(modal, typeId = null) {
        try {
            const name = modal.querySelector('#doctype-name').value.trim();
            const description = modal.querySelector('#doctype-description').value.trim();
            const personaSelect = modal.querySelector('#doctype-persona');
            const persona = personaSelect ? personaSelect.value : '';
            const planPrompt = modal.querySelector('#doctype-plan-prompt').value.trim();
            const actPrompt = modal.querySelector('#doctype-act-prompt').value.trim();

            console.log('DEBUG: Form values:', { 
                name, 
                description, 
                persona, 
                personaSelect: personaSelect ? 'found' : 'NOT FOUND',
                planPrompt: planPrompt.substring(0, 30), 
                actPrompt: actPrompt.substring(0, 30) 
            });

            if (!name || !description || !persona || !planPrompt || !actPrompt) {
                console.log('Validation failed:', { name: !!name, description: !!description, persona: !!persona, planPrompt: !!planPrompt, actPrompt: !!actPrompt });
                alert('Name, description, persona, plan prompt, and act prompt are required');
                return;
            }

            const data = { 
                name, 
                description, 
                persona,
                planPrompt,
                actPrompt
            };

            const isEdit = !!typeId;
            const url = isEdit ? `/api/document-types/${typeId}` : '/api/document-types';
            const method = isEdit ? 'PUT' : 'POST';

            const response = await AuthManager.fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });

            if (response.ok) {
                document.body.style.overflow = '';
                document.body.removeChild(modal);
                this.showSuccessMessage(`Document type ${isEdit ? 'updated' : 'created'} successfully!`);

                // Refresh configuration
                await this.loadConfiguration();
                
                // Refresh settings modal if open
                const settingsModal = document.querySelector('.settings-modal');
                if (settingsModal) {
                    settingsModal.querySelector('#document-types-tab').innerHTML = this.renderDocumentTypesSettings();
                    this.setupDocumentTypeEvents(settingsModal);
                }

                // Refresh home page document type cards if visible
                this.refreshHomePageDocumentTypes();
            } else {
                const error = await response.json();
                throw new Error(error.error || 'Failed to save document type');
            }
        } catch (error) {
            console.error('Error saving document type:', error);
            alert(`Failed to ${typeId ? 'update' : 'create'} document type: ${error.message}`);
        }
    }

    async deleteDocumentType(typeId) {
        if (!confirm('Are you sure you want to delete this custom document type?')) {
            return;
        }

        try {
            const response = await AuthManager.fetch(`/api/document-types/${typeId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                this.showSuccessMessage('Document type deleted successfully!');

                // Refresh configuration
                await this.loadConfiguration();
                
                // Refresh settings modal if open
                const settingsModal = document.querySelector('.settings-modal');
                if (settingsModal) {
                    settingsModal.querySelector('#document-types-tab').innerHTML = this.renderDocumentTypesSettings();
                    this.setupDocumentTypeEvents(settingsModal);
                }

                // Refresh home page document type cards if visible
                this.refreshHomePageDocumentTypes();
            } else {
                const error = await response.json();
                throw new Error(error.error || 'Failed to delete document type');
            }
        } catch (error) {
            console.error('Error deleting document type:', error);
            alert(`Failed to delete document type: ${error.message}`);
        }
    }

    async cloneDocumentType(typeId) {
        try {
            // Fetch the built-in type
            const response = await AuthManager.get(`/api/document-types/${typeId}`);
            if (!response.ok) {
                throw new Error('Failed to load document type');
            }
            const docType = await response.json();

            // Open editor with cloned data
            this.showDocumentTypeEditor(null, docType);
        } catch (error) {
            console.error('Error cloning document type:', error);
            alert(`Failed to clone document type: ${error.message}`);
        }
    }

    setupDocumentTypeEvents(modal) {
        // Clone button
        modal.querySelectorAll('.clone-document-type-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.cloneDocumentType(btn.dataset.id);
            });
        });

        // Edit button
        modal.querySelectorAll('.edit-document-type-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.showDocumentTypeEditor(btn.dataset.id);
            });
        });

        // Delete button
        modal.querySelectorAll('.delete-document-type-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.deleteDocumentType(btn.dataset.id);
            });
        });
    }

}

// Export as ES module
export { SettingsManager };